import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  Landmark,
  BadgePercent,
  FileCheck,
  Zap,
  Building,
  IndianRupee,
  FileText,
  ShieldCheck,
  ArrowRight,
  CheckCircle,
  MapPin,
} from "lucide-react";
import { Button } from "./ui/button";

const centralPolicies = [
  {
    icon: BadgePercent,
    title: "FAME II Subsidy",
    value: "Up to 50%",
    description: "Capital subsidy on charging infrastructure under Faster Adoption and Manufacturing of Electric Vehicles scheme",
    color: "primary",
  },
  {
    icon: Landmark,
    title: "GST Benefits",
    value: "5% Rate",
    description: "Reduced GST on EV chargers and components vs 18-28% on conventional equipment",
    color: "secondary",
  },
  {
    icon: FileCheck,
    title: "Fast-Track Approvals",
    value: "30 Days",
    description: "Expedited licensing and NOC clearances for EV charging infrastructure",
    color: "primary",
  },
  {
    icon: Zap,
    title: "EV Tariff",
    value: "Rs 6/kWh",
    description: "Special concessional electricity tariff for EV charging stations",
    color: "secondary",
  },
];

const statePolicies = [
  {
    state: "Maharashtra",
    code: "MH",
    highlight: true,
    benefits: [
      "25% capital subsidy (up to Rs 10 Lakh)",
      "100% exemption on electricity duty",
      "No road tax on EVs till 2025",
      "Priority grid connection",
    ],
    color: "primary",
  },
  {
    state: "Delhi",
    code: "DL",
    highlight: false,
    benefits: [
      "100% road tax exemption",
      "Rs 6,000/kW subsidy on chargers",
      "Waiver of registration fees",
      "Interest subvention on loans",
    ],
    color: "secondary",
  },
  {
    state: "Gujarat",
    code: "GJ",
    highlight: false,
    benefits: [
      "Rs 10,000/kW for DC chargers",
      "Capital subsidy up to Rs 10 Lakh",
      "5-year electricity duty exemption",
      "Land at concessional rates",
    ],
    color: "accent",
  },
  {
    state: "Karnataka",
    code: "KA",
    highlight: false,
    benefits: [
      "15% capital subsidy",
      "Exemption from permit requirements",
      "Priority power allocation",
      "Special EV zones in cities",
    ],
    color: "secondary",
  },
];

const supportServices = [
  {
    icon: FileText,
    title: "Subsidy Application",
    description: "Complete documentation and filing for FAME II and state subsidies",
  },
  {
    icon: ShieldCheck,
    title: "NOC & Licenses",
    description: "Fire safety, electrical, and municipal clearances handled",
  },
  {
    icon: Building,
    title: "Land Assistance",
    description: "Help with site selection and lease agreements",
  },
  {
    icon: Landmark,
    title: "Policy Updates",
    description: "Stay informed about new schemes and benefits",
  },
];

const GovernmentPolicies = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });

  return (
    <section
      id="govt-policies"
      className="py-24 md:py-32 bg-background relative overflow-hidden"
      data-testid="govt-policies-section"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[200px]" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-secondary/5 rounded-full blur-[150px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10" ref={containerRef}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-primary font-medium text-sm uppercase tracking-wider">
            Government Support
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-foreground mt-4">
            POLICIES & SUBSIDIES
            <br />
            <span className="text-primary">FOR EV INFRASTRUCTURE</span>
          </h2>
          <p className="text-foreground-muted text-lg mt-6 max-w-2xl mx-auto">
            Take advantage of central and state government incentives designed to 
            accelerate EV adoption. We help you maximize every benefit.
          </p>
        </motion.div>

        {/* Central Government Policies */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-16"
        >
          <h3 className="font-heading text-xl text-foreground mb-8 flex items-center gap-3">
            <Landmark className="w-6 h-6 text-primary" />
            Central Government Incentives
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {centralPolicies.map((policy, index) => (
              <motion.div
                key={policy.title}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.2 + index * 0.1 }}
                className="glass-card group hover:border-primary/50 transition-all"
                data-testid={`central-policy-${index}`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                  policy.color === "primary" ? "bg-primary/10" : "bg-secondary/10"
                }`}>
                  <policy.icon className={`w-6 h-6 ${
                    policy.color === "primary" ? "text-primary" : "text-secondary"
                  }`} />
                </div>
                <p className={`font-heading text-3xl ${
                  policy.color === "primary" ? "text-primary" : "text-secondary"
                }`}>
                  {policy.value}
                </p>
                <h4 className="font-medium text-foreground mt-2">{policy.title}</h4>
                <p className="text-foreground-muted text-sm mt-2">{policy.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* State-wise Policies */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-16"
        >
          <h3 className="font-heading text-xl text-foreground mb-8 flex items-center gap-3">
            <MapPin className="w-6 h-6 text-secondary" />
            State-wise EV Policies
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {statePolicies.map((state, index) => (
              <motion.div
                key={state.state}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 0.4 + index * 0.1 }}
                className={`glass-card relative ${
                  state.highlight ? "border-primary md:col-span-2 lg:col-span-1" : ""
                }`}
                data-testid={`state-policy-${state.code.toLowerCase()}`}
              >
                {state.highlight && (
                  <div className="absolute -top-3 -right-3 px-3 py-1 bg-primary text-black text-xs font-bold rounded-full flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> YOUR STATE
                  </div>
                )}
                
                <div className="flex items-start gap-4 mb-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center font-heading text-lg ${
                    state.highlight 
                      ? "bg-primary text-black" 
                      : state.color === "secondary" 
                        ? "bg-secondary/20 text-secondary"
                        : "bg-accent/20 text-accent"
                  }`}>
                    {state.code}
                  </div>
                  <div>
                    <h4 className="font-heading text-xl text-foreground">{state.state}</h4>
                    <p className="text-foreground-muted text-sm">EV Policy Benefits</p>
                  </div>
                </div>

                <ul className="space-y-2">
                  {state.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                        state.highlight ? "text-primary" : "text-foreground-muted"
                      }`} />
                      <span className="text-foreground-muted text-sm">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* We Handle Everything */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="glass-card border-primary/30"
        >
          <div className="text-center mb-10">
            <h3 className="font-heading text-2xl text-foreground">
              We Handle All <span className="text-primary">Government Procedures</span>
            </h3>
            <p className="text-foreground-muted mt-2">
              From subsidy applications to regulatory approvals - our team ensures you get 
              maximum benefits with zero hassle.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {supportServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.6 + index * 0.1 }}
                className="text-center p-4 rounded-xl bg-background-subtle hover:bg-background-paper transition-colors group"
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3 group-hover:bg-primary/20 transition-colors">
                  <service.icon className="w-6 h-6 text-primary" />
                </div>
                <h4 className="font-medium text-foreground text-sm">{service.title}</h4>
                <p className="text-foreground-muted text-xs mt-1">{service.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Button
              onClick={() => {
                const element = document.querySelector("#franchise");
                if (element) element.scrollIntoView({ behavior: "smooth" });
              }}
              className="rounded-full bg-primary text-black font-bold hover:shadow-[0_0_20px_rgba(204,255,0,0.5)] transition-all px-8 h-12"
              data-testid="govt-policies-cta"
            >
              <IndianRupee className="w-5 h-5 mr-2" />
              Get Subsidy Assistance
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </motion.div>

        {/* Bottom Note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.8 }}
          className="text-center text-foreground-muted text-sm mt-8"
        >
          * Policy benefits are subject to government guidelines and may vary. 
          Contact us for the latest updates on applicable schemes.
        </motion.p>
      </div>
    </section>
  );
};

export default GovernmentPolicies;
